import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Receipt, DollarSign, Printer, User, Phone, ShoppingBag, Store, Tag, FileText, Calendar } from 'lucide-react';
import { PageHeader, PrintPreviewModal, InfoCard } from '../../components/common';
import { applyCreditNote, cancelCreditNote, getCreditNote } from '../../services/api/offlineSales/creditNotes';
import { getOrders } from '../../services/api/offlineSales/orders';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/orderCalculations';
import { exportCreditNotePDF } from '../../utils/offlineOrderPdfExporter';
import { getCssVar } from '../../utils/themeColors';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import CreditNoteA4Document from './CreditNoteA4Document';
import ps from '../procurement/components/ProcurementDetails.module.css';
import styles from './CreditNoteDetailsPage.module.css';
import receiptStyles from '../offlineSales/components/OrderReceiptPrintModal.module.css';

const STATUS_COLORS = {
  issued: 'info',
  applied: 'success',
  cancelled: 'error',
};

const Badge = ({ type, label }) => (
  <span className={`${styles.badge} ${styles[`badge_${type}`]}`}>{label}</span>
);

const CreditNoteDetailsPage = () => {
  const { creditNoteId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();

  const [creditNote, setCreditNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyOrders, setApplyOrders] = useState([]);
  const [applyOrderId, setApplyOrderId] = useState('');
  const [applyAmount, setApplyAmount] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showPrint, setShowPrint] = useState(false);

  const loadCreditNote = async () => {
    try {
      setLoading(true);
      const result = await getCreditNote(creditNoteId);
      setCreditNote(result);
      if (result.customer?.id) {
        const ordersResult = await getOrders({ customerId: result.customer.id, limit: 200 });
        const openOrders = (ordersResult.orders || []).filter(o => parseFloat(o.balanceAmount) > 0);
        setApplyOrders(openOrders);
        setApplyOrderId('');
      }
    } catch (err) {
      error(err?.response?.data?.message || t('creditNote.failedToLoadCreditNoteDetails'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCreditNote(); }, [creditNoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const remainingAmount = Math.max(0, Number(creditNote?.amount || 0) - Number(creditNote?.appliedAmount || 0));
  const selectedApplyOrder = applyOrders.find(o => o.id === Number(applyOrderId));
  const applyCeiling = Math.min(remainingAmount, selectedApplyOrder ? parseFloat(selectedApplyOrder.balanceAmount) : remainingAmount);

  useEffect(() => {
    if (remainingAmount > 0) setApplyAmount(String(remainingAmount));
  }, [remainingAmount]);

  const handleApply = async () => {
    const amount = Number(applyAmount);
    if (!selectedApplyOrder) {
      error(t('creditNote.originalOrderRequired'));
      return;
    }
    if (!amount || amount <= 0) {
      error(t('creditNote.amountMustBePositive'));
      return;
    }
    if (amount > remainingAmount) {
      error(t('creditNote.applyAmountExceedsRemaining'));
      return;
    }
    if (amount > parseFloat(selectedApplyOrder.balanceAmount)) {
      error(t('creditNote.applyAmountExceedsOrderBalance'));
      return;
    }
    try {
      setIsApplying(true);
      await applyCreditNote(creditNoteId, { orderId: Number(applyOrderId), amount });
      success(t('creditNote.creditNoteAppliedSuccessfully'));
      setApplyAmount('');
      await loadCreditNote();
    } catch (err) {
      error(err?.response?.data?.message || t('creditNote.failedToApplyCreditNote'));
    } finally {
      setIsApplying(false);
    }
  };

  const handleCancel = async () => {
    const confirmed = await confirm(t('creditNote.confirmCancel'));
    if (!confirmed) return;
    try {
      setIsCancelling(true);
      await cancelCreditNote(creditNoteId);
      success(t('creditNote.creditNoteCancelledSuccessfully'));
      await loadCreditNote();
    } catch (err) {
      error(err?.response?.data?.message || t('creditNote.failedToCancelCreditNote'));
    } finally {
      setIsCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (!creditNote) {
    return <div className={styles.notFound}>{t('errors.notFound')}</div>;
  }

  const handlePrint = () => setShowPrint(true);

  const renderReceipt = () => (
    <div className={receiptStyles.receiptArea}>
      <h2 className={receiptStyles.receiptTitle}>{t('creditNote.creditNoteNumber')}</h2>
      <div className={receiptStyles.receiptMeta}>
        <div className={receiptStyles.receiptMetaRow}>
          <span>#</span>
          <span>{creditNote.creditNoteNumber}</span>
        </div>
        <div className={receiptStyles.receiptMetaRow}>
          <span>{t('common.created')}</span>
          <span>{new Date(creditNote.createdAt).toLocaleString()}</span>
        </div>
        <div className={receiptStyles.receiptMetaRow}>
          <span>{t('creditNote.customer')}</span>
          <span>{creditNote.customer?.name || t('common.na')}</span>
        </div>
      </div>
      <hr className={receiptStyles.receiptDivider} />
      <div className={receiptStyles.receiptTotals}>
        <div className={receiptStyles.receiptTotalRow}>
          <span>{t('creditNote.amount')}</span>
          <span>{formatCurrency(creditNote.amount)}</span>
        </div>
        <div className={receiptStyles.receiptTotalRow}>
          <span>{t('creditNote.appliedAmount')}</span>
          <span>{formatCurrency(creditNote.appliedAmount)}</span>
        </div>
        <div className={`${receiptStyles.receiptTotalRow} ${receiptStyles.receiptGrandTotal}`}>
          <strong>{t('creditNote.remainingAmount')}</strong>
          <strong>{formatCurrency(remainingAmount)}</strong>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title={creditNote.creditNoteNumber}
        icon={null}
        breadcrumbs={
          <button onClick={() => navigate('/offline-sales/credit-notes')} className={styles.backBtn}>
            <ChevronLeft size={16} /> {t('creditNote.allCreditNotes')}
          </button>
        }
      />

      <div className={styles.applyActions}>
        <button type="button" className={styles.secondaryBtn} onClick={handlePrint}>
          <Printer size={15} /> {t('common.printReceipt', 'Print Receipt')}
        </button>
        {(remainingAmount > 0 || Number(creditNote.appliedAmount) === 0) && creditNote.status !== 'cancelled' && (
          <>
            {remainingAmount > 0 && (
              <>
                <div className={styles.orderSelectWrapper}>
                  <Dropdown
                    value={applyOrderId}
                    onChange={value => setApplyOrderId(value || '')}
                    options={applyOrders.map(o => ({ value: o.id, label: `${o.orderNumber} (${formatCurrency(o.balanceAmount)})` }))}
                    placeholder={t('creditNote.selectOriginalOrder')}
                    allowClear={false}
                    searchable
                    size="compact"
                  />
                </div>
                <input
                  type="number"
                  min="0.01"
                  max={applyCeiling}
                  step="0.01"
                  value={applyAmount}
                  onChange={event => setApplyAmount(event.target.value)}
                  placeholder={t('creditNote.applyAmount')}
                  aria-label={t('creditNote.applyAmount')}
                />
                <button type="button" className={styles.applyButton} onClick={handleApply} disabled={isApplying || !selectedApplyOrder}>
                  {t('creditNote.applyCreditNote')}
                </button>
              </>
            )}
            {Number(creditNote.appliedAmount) === 0 && (
              <button type="button" className={styles.cancelButton} onClick={handleCancel} disabled={isCancelling}>
                {t('creditNote.cancelCreditNote')}
              </button>
            )}
          </>
        )}
      </div>

      <div className={styles.grid}>
        <div className={styles.column}>
          <InfoCard icon={Receipt} title={t('creditNote.creditNoteNumber')}>
            <div className={ps.infoGrid}>
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('common.status')}</span>
                <span className={ps.infoValue}>
                  <Badge type={STATUS_COLORS[creditNote.status]} label={t(`creditNote.status_${creditNote.status}`)} />
                </span>
              </div>
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('creditNote.customer')}</span>
                <span className={ps.infoValue}><User size={14} />{creditNote.customer?.name || t('common.na')}</span>
              </div>
              {creditNote.customer?.phone && (
                <div className={ps.infoItem}>
                  <span className={ps.infoLabel}>{t('offlineSales.customerPhone')}</span>
                  <span className={ps.infoValue}><Phone size={14} />{creditNote.customer.phone}</span>
                </div>
              )}
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('creditNote.originalOrder')}</span>
                <span className={ps.infoValue}>
                  <ShoppingBag size={14} />
                  {creditNote.originalOrder?.orderNumber ? (
                    <Link to={`/offline-sales/orders/${creditNote.originalOrder.id}`}>{creditNote.originalOrder.orderNumber}</Link>
                  ) : t('common.na')}
                </span>
              </div>
              {creditNote.originalOrder && (
                <div className={ps.infoItem}>
                  <span className={ps.infoLabel}>{t('offlineSales.balanceCol')}</span>
                  <span className={`${ps.infoValue} ${ps.amountCell}`}>
                    {formatCurrency(creditNote.originalOrder.balanceAmount)} / {formatCurrency(creditNote.originalOrder.grandTotal)}
                  </span>
                </div>
              )}
              {creditNote.originalOrder?.Shop?.name && (
                <div className={ps.infoItem}>
                  <span className={ps.infoLabel}>{t('common.shop')}</span>
                  <span className={ps.infoValue}><Store size={14} />{creditNote.originalOrder.Shop.name}</span>
                </div>
              )}
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('creditNote.sourceType')}</span>
                <span className={ps.infoValue}><Tag size={14} />{t(`creditNote.sourceType_${creditNote.sourceType}`)}</span>
              </div>
              {creditNote.reason && (
                <div className={`${ps.infoItem} ${ps.fullWidth}`}>
                  <span className={ps.infoLabel}>{t('creditNote.reason')}</span>
                  <p className={ps.notesText}>{creditNote.reason}</p>
                </div>
              )}
            </div>
          </InfoCard>

          <InfoCard icon={FileText} title={t('ecomOrders.additionalInformation')}>
            <div className={ps.infoList}>
              <div className={ps.infoListItem}>
                <label>{t('common.created')}</label>
                <p><Calendar size={14} />{formatDateTime(creditNote.createdAt)}</p>
              </div>
            </div>
          </InfoCard>
        </div>

        <InfoCard icon={DollarSign} title={t('creditNote.amount')}>
          <div className={ps.infoGrid}>
            <div className={`${ps.infoItem} ${ps.fullWidth}`}>
              <span className={ps.infoLabel}>{t('creditNote.amount')}</span>
              <span className={`${ps.infoValue} ${ps.infoValueLarge} ${ps.infoValueDanger}`}>{formatCurrency(creditNote.amount)}</span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('creditNote.appliedAmount')}</span>
              <span className={`${ps.infoValue} ${ps.amountCell}`}>{formatCurrency(creditNote.appliedAmount)}</span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('creditNote.remainingAmount')}</span>
              <span className={`${ps.infoValue} ${ps.amountCell}`}>{formatCurrency(remainingAmount)}</span>
            </div>
          </div>

          {(creditNote.offsetPayments || []).length > 0 && (
            <div className={styles.offsetBlock}>
              <div className={ps.tableWrapper}>
                <table className={ps.itemsTable}>
                  <tbody>
                    {creditNote.offsetPayments.map((payment) => (
                      <tr key={payment.id}>
                        <td><span className={ps.infoValue}><Calendar size={14} />{formatDate(payment.paymentDate || payment.paidAt || payment.createdAt)}</span></td>
                        <td className={`${ps.alignRight} ${ps.amountCell} ${ps.amountStrong}`}>{formatCurrency(payment.paymentAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </InfoCard>
      </div>

      {showPrint && (
        <PrintPreviewModal
          title={`Credit Note — ${creditNote.creditNoteNumber}`}
          onClose={() => setShowPrint(false)}
          pdfFileName={`Credit_Note_${creditNote.creditNoteNumber || creditNote.id}.pdf`}
          onDownloadPdf={() => exportCreditNotePDF(creditNote, {
            primary: getCssVar('--color-primary', '#4f46e5'),
            primaryDark: getCssVar('--color-primary-dark', '#3730a3'),
          })}
          renderReceipt={renderReceipt}
          renderA4={() => <CreditNoteA4Document creditNote={creditNote} />}
        />
      )}
    </div>
  );
};

export default CreditNoteDetailsPage;
